<template>
    <div
        class="lsn-loader-link"
        :class="{ 'lsn-loader-link--loading': isLoading }"
        tabindex="0"
    >
        <div class="lsn-loader-link__label">
            <!-- The default slot takes precedence over the "label" prop. -->
            <template v-if="hasDefaultSlot">
                <slot></slot>
            </template>

            <template v-else>
                {{ label }}
            </template>
        </div>
    </div>
</template>

<script setup>
import { computed, useSlots } from 'vue';

const props = defineProps({
    isLoading:
    {
        type:    Boolean,
        default: false,
    },

    label:
    {
        type:    String,
        default: 'link',
    },
});

const slots = useSlots();

const hasDefaultSlot = computed(() => !!slots.default?.().length);
</script>

<style scoped>
@reference "#main.css";

.lsn-loader-link
{
    @apply cursor-pointer inline-block outline-none select-none;

    &:focus-visible
    {
        @apply ring-1 ring-offset-2 ring-primary-500;
    }
}

.lsn-loader-link__label
{
    @apply text-primary-300 transition
        hover:text-primary-800;
}

.lsn-loader-link--danger > .lsn-loader-link__label
{
    @apply text-red-600 transition
        hover:text-red-800;
}

.lsn-loader-link--disabled > .lsn-loader-link__label
{
    @apply cursor-not-allowed text-gray-400 transition-none
        hover:text-gray-400;
}

.lsn-loader-link--loading
{
    @apply cursor-not-allowed;

    background-image: url("data:image/gif;base64,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");
    background-repeat: no-repeat;
    background-position: center center;
}

.lsn-loader-link--loading > .lsn-loader-link__label
{
    @apply invisible
}
</style>
